import type { CustomSeriesRenderItemParams, EChartsOption } from 'echarts';
import type { TopLevelFormatterParams } from 'echarts/types/dist/shared';
import { useMemo } from 'react';

import { valueToCategoryCoordinate } from '../../../store/ChartStore/heatmapGrid';
import type { HeatmapBinIndex, HeatmapDataPoint } from '../../../store/ChartStore/types';
import type { HeatmapWidgetAxisParams } from '../../../store/types';
import type { HeatmapCorridorOverlay } from '../../../types';
import { HEATMAP_THEME_STYLES } from '../heatmapThemeConfig';

const VALUE_DIMENSIONS: Record<'rop' | 'density' | 'mse', number> = {
  rop: 2,
  mse: 3,
  density: 4,
};

const VISUAL_MAP_BAR_WIDTH = 8;
const VISUAL_MAP_CHART_GAP = 12;
const VISUAL_MAP_TEXT_GAP = 20;
const VISUAL_MAP_CHAR_WIDTH = 8;
const VISUAL_MAP_RIGHT = 22;
const VISUAL_MAP_HANDLE_SIZE = '200%';
const VISUAL_MAP_INDICATOR_SIZE = '84%';
// Half of the handle width increase from ECharts' 120% default.
const VISUAL_MAP_HANDLE_SHIFT = 3.2;
const GRID_LEFT = 40;
const GRID_TOP = 16;
const GRID_BOTTOM = 32;
const VISUAL_MAP_HEIGHT_OFFSET = 20;

type UseHeatmapChartOptionsParams = {
  data: HeatmapDataPoint[];
  activeBins: HeatmapBinIndex[];
  yAxisValues: number[];
  xAxisValues: number[];
  zAxisParams: Omit<HeatmapWidgetAxisParams, 'step'>;
  yAxisParams: HeatmapWidgetAxisParams;
  xAxisParams: HeatmapWidgetAxisParams;
  xAxisName: string;
  yAxisName: string;
  containerHeight: number;
  themeKey: keyof typeof HEATMAP_THEME_STYLES;
  valueField: 'rop' | 'density' | 'mse';
  zAxisNameTranslated: string;
  suppressTooltips?: boolean;
  corridorOverlay?: HeatmapCorridorOverlay | null;
};

const formatNumber = (value: number): string => (Number.isInteger(value) ? value.toString() : value.toFixed(1));

const formatValueWithUnit = (value: number | string, unit?: { abbreviation: string } | null): string => {
  const unitSuffix = unit?.abbreviation ? ` ${unit.abbreviation}` : '';
  return `${value}${unitSuffix}`;
};

const calculateVisualMapDimensions = (zAxisParams: Omit<HeatmapWidgetAxisParams, 'step'>, containerHeight: number) => {
  const hasIntegerBounds = Number.isInteger(zAxisParams.min) && Number.isInteger(zAxisParams.max);
  const decimalPlaces = hasIntegerBounds ? 0 : 1;

  const formatValue = (value: number) => (Number.isFinite(value) ? value.toFixed(decimalPlaces) : value.toString());

  const minLabel = formatValue(zAxisParams.min);
  const maxLabel = formatValue(zAxisParams.max);
  const labelChars = Math.max(minLabel.length, maxLabel.length, 3);

  const labelWidth = labelChars * VISUAL_MAP_CHAR_WIDTH;

  const gridRight =
    VISUAL_MAP_CHART_GAP +
    VISUAL_MAP_BAR_WIDTH +
    VISUAL_MAP_TEXT_GAP +
    labelWidth +
    VISUAL_MAP_HANDLE_SHIFT +
    VISUAL_MAP_RIGHT;

  const visualMapHeight = Math.max(containerHeight - GRID_TOP - GRID_BOTTOM - VISUAL_MAP_HEIGHT_OFFSET, 0);
  return {
    gridRight,
    visualMapHeight,
    labelWidth,
    formatValue,
  };
};

const clampTooltipPosition = (point: [number, number], size?: { viewSize: number[]; contentSize: number[] }) => {
  const PADDING = 8;
  const [x, y] = point;
  const [viewWidth, viewHeight] = size?.viewSize ?? [0, 0];
  const [contentWidth, contentHeight] = size?.contentSize ?? [0, 0];

  const clampedX = Math.min(Math.max(x, PADDING), Math.max(PADDING, viewWidth - contentWidth - PADDING));
  const clampedY = Math.min(Math.max(y, PADDING), Math.max(PADDING, viewHeight - contentHeight - PADDING));

  return [clampedX, clampedY];
};

const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, (character) => {
    const entities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return entities[character];
  });

export const createTooltipFormatter = (
  xAxisValues: number[],
  yAxisValues: number[],
  xAxisName: string,
  yAxisName: string,
  xAxisParams: HeatmapWidgetAxisParams,
  yAxisParams: HeatmapWidgetAxisParams,
  zAxisParams: Omit<HeatmapWidgetAxisParams, 'step'>,
  valueField: 'rop' | 'density' | 'mse',
  zAxisName: string,
) => {
  return (formatterParams: TopLevelFormatterParams) => {
    const params = Array.isArray(formatterParams) ? formatterParams[0] : formatterParams;
    if (!Array.isArray(params.value)) {
      return '';
    }
    const [xIndex, yIndex, ropValue, mseValue, pointsInBin] = params.value;

    const formatAxisRange = (
      axisValues: number[],
      axisName: string,
      axisParams: HeatmapWidgetAxisParams,
      index: number,
    ) => {
      const upperBound = axisValues[index] ?? axisParams.max;
      const lowerBound = index <= 0 ? axisParams.min : (axisValues[index - 1] ?? axisParams.min);

      const unitSuffix = axisParams.unit?.abbreviation ? ` ${axisParams.unit.abbreviation}` : '';

      return escapeHtml(`${axisName}: ${formatNumber(lowerBound)} - ${formatNumber(upperBound)}${unitSuffix}`);
    };

    const formatZValue = () => {
      const rawValue = valueField === 'rop' ? ropValue : valueField === 'mse' ? mseValue : pointsInBin;

      if (rawValue === null || rawValue === undefined) {
        return '';
      }
      const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue);
      const formatted = formatNumber(numericValue);

      return escapeHtml(`${zAxisName}: ${formatValueWithUnit(formatted, zAxisParams.unit)}`);
    };

    if (typeof xIndex !== 'number' || typeof yIndex !== 'number') {
      return '';
    }

    return [
      formatAxisRange(xAxisValues, xAxisName, xAxisParams, xIndex),
      formatAxisRange(yAxisValues, yAxisName, yAxisParams, yIndex),
      formatZValue(),
    ].join('<br/>');
  };
};

export const useHeatmapChartOptions = ({
  data,
  activeBins,
  yAxisValues,
  xAxisValues,
  zAxisParams,
  yAxisParams,
  xAxisParams,
  xAxisName,
  yAxisName,
  containerHeight,
  themeKey,
  valueField,
  zAxisNameTranslated,
  suppressTooltips,
  corridorOverlay,
}: UseHeatmapChartOptionsParams): EChartsOption => {
  const { primary, backgroundColor, brightColor } = HEATMAP_THEME_STYLES[themeKey];

  return useMemo<EChartsOption>(() => {
    const seriesData = data.map(({ xIndex, yIndex, ropQuantile, mseQuantile, pointsInBin }) => [
      xIndex,
      yIndex,
      ropQuantile,
      mseQuantile,
      valueField === 'density' && pointsInBin <= 0 ? null : pointsInBin,
    ]);
    const activeBinsSeriesData = activeBins.map(({ xIndex, yIndex }) => [xIndex, yIndex]);

    const valueDimension = VALUE_DIMENSIONS[valueField];
    const themeStyles = HEATMAP_THEME_STYLES[themeKey];
    const { gridRight, visualMapHeight, labelWidth, formatValue } = calculateVisualMapDimensions(
      zAxisParams,
      containerHeight,
    );

    const grid = {
      left: GRID_LEFT,
      right: gridRight,
      top: GRID_TOP,
      bottom: GRID_BOTTOM,
    };

    const zAxisLabel = zAxisParams.unit?.abbreviation
      ? `${zAxisNameTranslated}, ${zAxisParams.unit.abbreviation}`
      : zAxisNameTranslated;

    return {
      grid,
      tooltip: {
        show: !suppressTooltips,
        triggerOn: suppressTooltips ? 'none' : 'mousemove|click|mousewheel',
        borderRadius: 4,
        trigger: 'item',
        confine: true,
        position: (point: number[], _params, _dom, _rect, size) =>
          clampTooltipPosition(point as [number, number], size),
        formatter: createTooltipFormatter(
          xAxisValues,
          yAxisValues,
          xAxisName,
          yAxisName,
          xAxisParams,
          yAxisParams,
          zAxisParams,
          valueField,
          zAxisNameTranslated,
        ),
      },
      xAxis: [
        {
          type: 'category',
          data: xAxisValues,
          splitArea: { show: true, interval: 0 },
          boundaryGap: true,
          axisTick: { alignWithLabel: false },
          axisLabel: { show: false },
        },
        {
          type: 'value',
          min: -0.5,
          max: xAxisValues.length - 0.5,
          position: 'bottom',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            customValues: xAxisValues.map((_, index) => index + 0.5),
            formatter: (_value, index) => String(xAxisValues[index]),
            hideOverlap: true,
            color: themeStyles.visualMapLabelColor,
          },
          name: `${xAxisName}, ${xAxisParams.unit.abbreviation}`,
          nameLocation: 'middle',
          nameTextStyle: { color: themeStyles.visualMapLabelColor },
          axisPointer: { show: false },
        },
      ],
      yAxis: [
        {
          type: 'category',
          data: yAxisValues,
          splitArea: { show: true, interval: 0 },
          boundaryGap: true,
          axisTick: { alignWithLabel: false },
          axisLabel: { show: false },
        },
        {
          type: 'value',
          min: -0.5,
          max: yAxisValues.length - 0.5,
          position: 'left',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            customValues: yAxisValues.map((_, index) => index + 0.5),
            formatter: (_value, index) => String(yAxisValues[index]),
            hideOverlap: true,
            color: themeStyles.visualMapLabelColor,
          },
          name: `${yAxisName}, ${yAxisParams.unit.abbreviation}`,
          nameLocation: 'middle',
          nameTextStyle: { color: themeStyles.visualMapLabelColor },
          axisPointer: { show: false },
        },
      ],
      graphic: [
        {
          type: 'text',
          right: 4,
          top: grid.top + visualMapHeight / 2,
          rotation: (90 * Math.PI) / 180,
          origin: [0, 0],
          style: {
            text: zAxisLabel,
            fontSize: 12,
            fontWeight: 500,
            fill: themeStyles.visualMapLabelColor,
            textAlign: 'center',
            fontFamily: 'Inter, "Segoe UI", sans-serif',
          },
          cursor: 'default',
        },
      ],
      visualMap: {
        type: 'continuous',
        seriesIndex: 0,
        calculable: true,
        hoverLink: !suppressTooltips,
        min: zAxisParams.min,
        max: zAxisParams.max,
        range: [zAxisParams.min, zAxisParams.max],
        handleSize: VISUAL_MAP_HANDLE_SIZE,
        indicatorSize: VISUAL_MAP_INDICATOR_SIZE,
        formatter: (v) => (typeof v === 'number' ? formatValue(v) : ''),
        orient: 'vertical',
        align: 'left',
        dimension: valueDimension,
        textGap: VISUAL_MAP_TEXT_GAP,
        top: 0,
        bottom: grid.bottom,
        itemWidth: VISUAL_MAP_BAR_WIDTH,
        right: VISUAL_MAP_RIGHT,
        ...(visualMapHeight > 0 && {
          height: visualMapHeight,
          itemHeight: visualMapHeight,
        }),
        inRange: {
          color:
            valueField !== 'mse'
              ? ['rgba(212, 51, 21, 1)', 'rgba(255, 216, 59, 1)', 'rgba(104, 223, 26, 1)']
              : ['rgba(104, 223, 26, 1)', 'rgba(255, 216, 59, 1)', 'rgba(212, 51, 21, 1)'],
        },
        textStyle: {
          rich: {
            valueLabel: {
              width: labelWidth,
              align: 'right',
              color: themeStyles.visualMapLabelColor,
            },
          },
        },
      },
      series: [
        // Stable IDs preserve series during replaceMerge and avoid replaying entry animations on hover.
        {
          id: 'heatmap-bins',
          type: 'heatmap',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: seriesData,
          encode: { value: valueDimension },
          itemStyle: {
            borderWidth: 1,
            borderColor: themeStyles.cellBorderColor,
          },
          emphasis: {
            itemStyle: {
              dropShadowBlur: 20,
              borderWidth: 0,
              shadowColor: 'rgba(28, 33, 46, 0.5)',
              shadowOffsetX: 1,
              shadowOffsetY: 5,
            },
          },
        },
        {
          id: 'heatmap-corridor',
          type: 'custom',
          xAxisIndex: 1,
          yAxisIndex: 1,
          silent: true,
          clip: false,
          z: 4,
          data: corridorOverlayToSeriesData(corridorOverlay),
          renderItem: (params, api) => {
            const xLow = Number(api.value(0));
            const yLow = Number(api.value(1));
            const xHigh = Number(api.value(2));
            const yHigh = Number(api.value(3));

            const p0 = api.coord([
              valueToCategoryCoordinate(xLow, xAxisParams),
              valueToCategoryCoordinate(yLow, yAxisParams),
            ]);
            const p1 = api.coord([
              valueToCategoryCoordinate(xHigh, xAxisParams),
              valueToCategoryCoordinate(yHigh, yAxisParams),
            ]);
            if (!p0 || !p1 || ![...p0, ...p1].every(Number.isFinite)) {
              return;
            }

            const coordSys = params.coordSys;
            if (!ifCartesian2d(coordSys)) {
              return;
            }

            const left = Math.min(p0[0], p1[0]);
            const right = Math.max(p0[0], p1[0]);
            const top = Math.min(p0[1], p1[1]);
            const bottom = Math.max(p0[1], p1[1]);

            const makeBadge = (cx: number, cy: number, value: number) => ({
              type: 'text' as const,
              style: {
                text: formatNumber(value),
                x: cx,
                y: cy,
                fill: brightColor,
                fontSize: 11,
                lineHeight: 16,
                fontWeight: 600 as const,
                fontFamily: 'Inter, sans-serif',
                textAlign: 'center' as const,
                textVerticalAlign: 'middle' as const,
                backgroundColor: backgroundColor,
                borderColor: primary,
                borderWidth: 1,
                borderRadius: 2,
                padding: [0, 6],
              },
            });

            const badges = [
              makeBadge(left, coordSys.y + coordSys.height + 12, xLow),
              makeBadge(right, coordSys.y + coordSys.height + 12, xHigh),
              makeBadge(coordSys.x - 18, bottom, yLow),
              makeBadge(coordSys.x - 18, top, yHigh),
            ];

            return {
              type: 'group',
              children: [
                {
                  type: 'rect',
                  shape: {
                    x: left,
                    y: top,
                    width: right - left,
                    height: bottom - top,
                    r: 3,
                  },
                  style: {
                    fill: 'transparent',
                    stroke: primary,
                    lineWidth: 3,
                  },
                },
                ...badges,
              ],
            };
          },
        },
        {
          id: 'heatmap-active-bins',
          type: 'scatter',
          xAxisIndex: 0,
          yAxisIndex: 0,
          silent: true,
          symbol: 'circle',
          symbolSize: 8,
          data: activeBinsSeriesData,
          animation: true,
          animationDuration: 100,
          animationEasing: 'cubicOut',
          animationDurationUpdate: 180,
          animationEasingUpdate: 'quadraticOut',
          itemStyle: {
            color: themeStyles.activeBinColor,
          },
          z: 3,
        },
      ],
    };
  }, [
    data,
    activeBins,
    valueField,
    themeKey,
    primary,
    zAxisParams,
    containerHeight,
    zAxisNameTranslated,
    suppressTooltips,
    xAxisValues,
    yAxisValues,
    xAxisName,
    yAxisName,
    xAxisParams,
    yAxisParams,
    corridorOverlay,
    brightColor,
    backgroundColor,
  ]);
};

type CoordinateSystemDataLayout = {
  type: 'cartesian2d';
  x: number;
  y: number;
  width: number;
  height: number;
};

function ifCartesian2d(coordSys: CustomSeriesRenderItemParams['coordSys'] | CoordinateSystemDataLayout): coordSys is {
  type: 'cartesian2d';
  x: number;
  y: number;
  width: number;
  height: number;
} {
  return coordSys.type === 'cartesian2d';
}

function corridorOverlayToSeriesData(
  overlay: HeatmapCorridorOverlay | null | undefined,
): [number, number, number, number][] {
  if (!overlay) {
    return [];
  }

  const xLow = Math.min(overlay.x.floor, overlay.x.ceiling);
  const xHigh = Math.max(overlay.x.floor, overlay.x.ceiling);
  const yLow = Math.min(overlay.y.floor, overlay.y.ceiling);
  const yHigh = Math.max(overlay.y.floor, overlay.y.ceiling);

  return [[xLow, yLow, xHigh, yHigh]];
}
